'use client';

import { BaseMap, JourneyLayers, UserTilesSource } from '@streetferret/map-web';
import { StreetFerretLogoWithText } from '@streetferret/ui-web/brand';
import { Button } from '@streetferret/ui-web/button';
import { useTheme } from 'next-themes';
import { useMemo } from 'react';
import {
  GeolocateControl,
  Marker,
  type LngLatBoundsLike,
  type MapLayerMouseEvent,
} from 'react-map-gl';
import { JourneyLiveLayers } from './journey-live-layers';
import { type LiveMapOptions } from './live-map-options';
import {
  JourneyWaypointMarkers,
  type JourneyWaypointMarker,
} from './journey-waypoint-markers';
import { RouteStartEndMarkers } from './route-start-end-markers';
import { useSearchParams } from 'next/navigation';

interface LiveMapProps {
  stravaId: number;
  tileVersion: number;
  token: string;
  bounds?: LngLatBoundsLike;
  journeyId: bigint;
  user: {
    firstname: string | null;
    lastname: string | null;
    profile: string | null;
  };
  mapStyleConfig?: {
    light: string;
    dark: string;
  };
  startDate: Date;
  startPoint?: {
    lat: number;
    lng: number;
  };
  endPoint?: {
    lat: number;
    lng: number;
  };
  waypoints?: JourneyWaypointMarker[];
  flagForceComplete?: boolean;
  options?: LiveMapOptions;
  onMapClick?: (lngLat: { lng: number; lat: number }) => void;
  mapCursor?: string;
  placementPreview?: { lat: number; lng: number } | null;
  children?: React.ReactNode;
}

export function LiveMap({
  stravaId,
  tileVersion,
  token,
  bounds,
  journeyId,
  mapStyleConfig,
  user,
  startDate,
  startPoint,
  endPoint,
  waypoints = [],
  flagForceComplete,
  options,
  onMapClick,
  mapCursor,
  placementPreview,
  children,
}: LiveMapProps) {
  const { resolvedTheme, forcedTheme } = useTheme();
  const searchParams = useSearchParams();
  const mockOffset = searchParams.get('mockOffset');
  const mockOffsetNumber = mockOffset ? Number(mockOffset) : undefined;
  const mockOffRoute = searchParams.get('mockOffRoute');
  const mockOffRouteNumber = mockOffRoute ? Number(mockOffRoute) : undefined;
  const noCache = searchParams.get('noCache');
  const noCacheBoolean = noCache ? noCache === 'true' : undefined;

  const baseTheme = useMemo(() => {
    if (forcedTheme) {
      return forcedTheme === 'dark' ? 'dark' : 'light';
    }
    return resolvedTheme === 'dark' ? 'dark' : 'light';
  }, [resolvedTheme, forcedTheme]);

  const mapStyle = useMemo(() => {
    if (!mapStyleConfig) {
      return undefined;
    }
    if (baseTheme === 'dark') {
      return mapStyleConfig?.dark;
    }
    return mapStyleConfig?.light;
  }, [baseTheme, mapStyleConfig]);

  const handleMapClick = (e: MapLayerMouseEvent) => {
    if (!onMapClick) return;
    onMapClick({ lng: e.lngLat.lng, lat: e.lngLat.lat });
  };

  return (
    <div
      className={`h-full w-full ${mapCursor === 'crosshair' ? 'cursor-crosshair [&_.maplibregl-canvas]:!cursor-crosshair' : ''}`}>
      <BaseMap
        type="base"
        baseTheme={baseTheme}
        initialViewState={
          bounds
            ? {
                bounds,
                fitBoundsOptions: {
                  padding: 30,
                },
              }
            : undefined
        }
        token={token}
        mapStyle={mapStyle}
        onClick={onMapClick ? handleMapClick : undefined}
        interactiveLayerIds={onMapClick ? undefined : ['journey-progress']}
        style={{ width: '100%', height: '100%' }}>
      {!options?.hideGeolocate && (
        <GeolocateControl
          style={{
            backgroundColor: 'rgb(var(--muted))',
          }}
          trackUserLocation
          showUserHeading
          onError={e => console.log(e)}
          positionOptions={{ enableHighAccuracy: true }}
          onOutOfMaxBounds={e => console.log('out of bounds', e)}
        />
      )}
      <UserTilesSource stravaId={stravaId} version={tileVersion}>
        <JourneyLayers
          journeyId={journeyId}
          flagForceComplete={flagForceComplete}
        />
      </UserTilesSource>
      <RouteStartEndMarkers
        startDate={startDate}
        startPoint={startPoint}
        endPoint={endPoint}
      />
      <JourneyLiveLayers
        journeyId={journeyId}
        user={user}
        flagForceComplete={flagForceComplete}
        mockOffset={
          mockOffsetNumber && mockOffsetNumber >= 0
            ? mockOffsetNumber
            : undefined
        }
        mockOffRoute={
          mockOffRouteNumber && mockOffRouteNumber >= 0
            ? mockOffRouteNumber
            : undefined
        }
        noCache={noCacheBoolean}
      />
      {waypoints.length > 0 ? (
        <JourneyWaypointMarkers waypoints={waypoints} />
      ) : null}
      {placementPreview ? (
        <Marker
          longitude={placementPreview.lng}
          latitude={placementPreview.lat}
          anchor="center">
          <div className="relative flex size-7 items-center justify-center rounded-full border-2 border-dashed border-primary bg-primary/30 text-primary-foreground shadow-md">
            <span className="text-xs font-semibold leading-none">+</span>
          </div>
        </Marker>
      ) : null}
      {options?.showStreetferretBranding && (
        <div className="absolute bottom-2 left-2">
          <Button variant={'ghost'} className="!px-1" asChild size={'lg'}>
            <a href="https://streetferret.com/journeys/about" target="_blank">
              <StreetFerretLogoWithText />
            </a>
          </Button>
        </div>
      )}
      {children}
      </BaseMap>
    </div>
  );
}

export function LiveMapErrorState() {
  return (
    <div className="h-full w-full bg-black">
      <div className="flex h-full w-full items-center justify-center">
        <div className="text-white">
          <p>Error loading live map</p>
        </div>
      </div>
    </div>
  );
}

export function LiveMapLoadingState() {
  return <div className="h-full w-full bg-black" />;
}
